import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getData } from '../../services/api';

const DEFAULT_EMAIL = 'superadmin@platform.com';
const DEFAULT_PASS  = 'SuperAdmin@123';

export default function SALogin() {
  const { loginSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  // Show the hardcoded "Default Credentials" hint ONLY when demo super-admin
  // mode is enabled on the backend. With DEMO_MODE off, the page must not leak
  // any credentials and must not auto-fill the form.
  const [demoSuperAdmin, setDemoSuperAdmin] = useState(false);
  useEffect(() => {
    getData('/config/demo-mode')
      .then(r => {
        const d = r?.data || r;
        const enabled = !!d?.superAdmin;
        setDemoSuperAdmin(enabled);
        if (enabled) setForm({ email: DEFAULT_EMAIL, password: DEFAULT_PASS });
      })
      .catch(() => setDemoSuperAdmin(false));
  }, []);

  const fillCredentials = () => {
    setForm({ email: DEFAULT_EMAIL, password: DEFAULT_PASS });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginSuperAdmin(form.email, form.password);
      navigate('/sa/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-glass" style={{ maxWidth: 420 }}>
      <div className="auth-logo" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
        <i className="bi bi-shield-lock-fill"></i>
      </div>
      <h4 className="text-center fw-bold mb-1">Platform Admin</h4>
      <p className="text-center mb-4" style={{ fontSize: 13, color: '#64748b' }}>
        Super Admin access — platform management only
      </p>

      {/* Default Credentials Hint — only visible when DEMO_MODE_SUPERADMIN is on */}
      {demoSuperAdmin && (
        <div className="cred-hint mb-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-info-circle-fill text-info" style={{ fontSize: 13 }}></i>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Demo Credentials</span>
            <button type="button" className="btn btn-sm ms-auto py-0 px-2" style={{ fontSize: 11, background: '#e0f2fe', border: 'none', color: '#0369a1', borderRadius: 5 }} onClick={fillCredentials}>
              Auto-fill
            </button>
          </div>
          <div className="cred-row">
            <span className="cred-label">Email</span>
            <span className="cred-value" onClick={() => setForm(f => ({ ...f, email: DEFAULT_EMAIL }))}>{DEFAULT_EMAIL}</span>
          </div>
          <div className="cred-row mt-1">
            <span className="cred-label">Password</span>
            <span className="cred-value" onClick={() => setForm(f => ({ ...f, password: DEFAULT_PASS }))}>{DEFAULT_PASS}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
          <i className="bi bi-exclamation-circle-fill"></i>{error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Email Address</label>
          <input
            type="email" className="form-control"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            required autoFocus
          />
        </div>
        <div className="mb-4">
          <label className="form-label">Password</label>
          <div className="input-group">
            <input
              type={showPass ? 'text' : 'password'}
              className="form-control"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              required
            />
            <button type="button" className="input-group-text" style={{ cursor: 'pointer', border: '1px solid #e2e8f0', borderLeft: 'none', background: '#f8fafc' }}
              onClick={() => setShowPass(p => !p)}>
              <i className={`bi ${showPass ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>
          </div>
        </div>
        <button
          type="submit"
          className="btn btn-primary w-100"
          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', padding: '10px', fontWeight: 700 }}
          disabled={loading}
        >
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2"></span>Signing in...</>
            : <><i className="bi bi-shield-check me-2"></i>Sign In as Super Admin</>
          }
        </button>
      </form>

      <div className="text-center mt-4" style={{ fontSize: 12.5, color: '#94a3b8' }}>
        <Link to="/login" className="text-muted text-decoration-none">
          <i className="bi bi-arrow-left me-1"></i>Back to tenant login
        </Link>
      </div>
    </div>
  );
}
